/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { CharacterLimitPlugin } from '@lexical/react/LexicalCharacterLimitPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import * as React from 'react';
import {
  RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { CAN_USE_DOM } from 'shared/canUseDOM';

import { useSettings } from './context/SettingsContext';
import { useSharedHistoryContext } from './context/SharedHistoryContext';
import AutocompletePlugin from './plugins/AutocompletePlugin';
import AutoEmbedPlugin from './plugins/AutoEmbedPlugin';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import CodeHighlightPlugin from './plugins/CodeHighlightPlugin';
import CollapsiblePlugin from './plugins/CollapsiblePlugin';
import ContextMenuPlugin from './plugins/ContextMenuPlugin';
import DragDropPaste from './plugins/DragDropPastePlugin';
import DraggableBlockPlugin from './plugins/DraggableBlockPlugin';
import EmojiPickerPlugin from './plugins/EmojiPickerPlugin';
import EmojisPlugin from './plugins/EmojisPlugin';
import FigmaPlugin from './plugins/FigmaPlugin';
import FloatingLinkEditorPlugin from './plugins/FloatingLinkEditorPlugin';
import FloatingTextFormatToolbarPlugin from './plugins/FloatingTextFormatToolbarPlugin';
import ImagesPlugin from './plugins/ImagesPlugin';
import InlineImagePlugin from './plugins/InlineImagePlugin';
import KeywordsPlugin from './plugins/KeywordsPlugin';
import { LayoutPlugin } from './plugins/LayoutPlugin/LayoutPlugin';
import LinkPlugin from './plugins/LinkPlugin';
import ListMaxIndentLevelPlugin from './plugins/ListMaxIndentLevelPlugin';
import MarkdownShortcutPlugin from './plugins/MarkdownShortcutPlugin';
import { MaxLengthPlugin } from './plugins/MaxLengthPlugin';
import MentionsPlugin from './plugins/MentionsPlugin';
import PageBreakPlugin from './plugins/PageBreakPlugin';
import PollPlugin from './plugins/PollPlugin';
import SpeechToTextPlugin from './plugins/SpeechToTextPlugin';
import TabFocusPlugin from './plugins/TabFocusPlugin';
import TableCellActionMenuPlugin from './plugins/TableActionMenuPlugin';
import TableCellResizer from './plugins/TableCellResizer';
import TableHoverActionsPlugin from './plugins/TableHoverActionsPlugin';
import TableOfContentsPlugin from './plugins/TableOfContentsPlugin';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import TwitterPlugin from './plugins/TwitterPlugin';
import YouTubePlugin from './plugins/YouTubePlugin';
import ContentEditable from './ui/ContentEditable';
import FirmwareTablePlugin from './plugins/FirmwareTablePlugin';
import CustomTextNodePlugin from './plugins/LocalizableTextPlugin.ts';
import { LexicalEditor } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useScroll } from '../../../../helpers/useScroll.ts';
import { CustomTranslateIconPlugin } from './plugins/CustomTranslateIconPlugin.tsx';
import clsx from 'clsx';
import { PageDesignerContext } from '../DesignerNewPage.tsx';
import { VersionList } from './components/VersionList.tsx';

export default function Editor({
  isPageDesigner,
  setActiveEditor,
  linkEditorRef,
}: {
  isPageDesigner: boolean;
  linkEditorRef: RefObject<HTMLDivElement | null>;
  setActiveEditor?: (editor: LexicalEditor) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const pageDesignerContext = useContext(PageDesignerContext);

  useEffect(() => {
    setActiveEditor?.(editor);
  }, []);

  const {
    thumbLeftPercent,
    thumbWidthPercent,
    onThumbMouseDown,
    trackRef,
    containerRef,
    visible,
  } = useScroll();

  const { historyState } = useSharedHistoryContext();
  const {
    setOption: setSetting,
    settings: {
      isCollab,
      isAutocomplete,
      isMaxLength,
      isCharLimit,
      isCharLimitUtf8,
      isRichText,
      showTreeView,
      showTableOfContents,
      shouldUseLexicalContextMenu,
      tableCellMerge,
      tableCellBackgroundColor,
    },
  } = useSettings();

  // if use useEffect, then Editor will render edit tools for a second, despite "isPageDesigner: false"
  // it happens because useEffect fires after render and that's why useLayoutEffect is needed
  // we want to set "isPageDesigner" before first render, so the edit tools won't be rendered
  useLayoutEffect(() => {
    setSetting('isPageDesigner', isPageDesigner, false);
  }, [isPageDesigner, setSetting]);

  const isEditable = useLexicalEditable();
  const placeholder = isCollab
    ? 'Enter some collaborative rich text...'
    : isRichText
    ? 'Enter some rich text...'
    : 'Enter some plain text...';
  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLDivElement | null>(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] =
    useState<boolean>(false);
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport =
        CAN_USE_DOM && window.matchMedia('(max-width: 1025px)').matches;

      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener('resize', updateViewPortWidth);

    return () => {
      window.removeEventListener('resize', updateViewPortWidth);
    };
  }, [isSmallWidthViewport]);

  return (
    <>
      {isRichText && <ToolbarPlugin setIsLinkEditMode={setIsLinkEditMode} />}
      <div className={'rowContainer'}>
        <div
          className={clsx(
            'editor-container',
            !isEditable && 'editor-container-readonly',
            showTreeView && 'tree-view',
            !isRichText && 'plain-text',
            pageDesignerContext?.isVersionsModeActive && 'marginTop64',
            pageDesignerContext?.isVersionsModeActive && 'editorShadow',
          )}
        >
          {isMaxLength && <MaxLengthPlugin maxLength={30} />}
          <DragDropPaste />
          <AutoFocusPlugin />
          <ClearEditorPlugin />
          <EmojiPickerPlugin />
          <AutoEmbedPlugin />
          <MentionsPlugin />
          <EmojisPlugin />
          <HashtagPlugin />
          <KeywordsPlugin />
          <SpeechToTextPlugin />
          <AutoLinkPlugin />
          {isRichText ? (
            <>
              <HistoryPlugin externalHistoryState={historyState} />
              <RichTextPlugin
                contentEditable={
                  <>
                    <div
                      ref={containerRef}
                      className={clsx(
                        'editor-scroller',
                        pageDesignerContext?.isVersionsModeActive &&
                          'padding24',
                      )}
                    >
                      <div
                        className="editor"
                        data-test-id="editor-container"
                        ref={onRef}
                      >
                        <ContentEditable
                          placeholder={isEditable ? placeholder : ''}
                        />
                      </div>
                    </div>

                    {visible && (
                      <div
                        ref={trackRef}
                        className={'scrollbar-track'}
                        onMouseDown={onThumbMouseDown}
                      >
                        <div
                          style={{
                            left: `${thumbLeftPercent}%`,
                            width: `${thumbWidthPercent}%`,
                          }}
                          className={'scrollbar-thumb'}
                        />
                      </div>
                    )}
                  </>
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
              <MarkdownShortcutPlugin />
              <CodeHighlightPlugin />
              <ListPlugin />
              <CheckListPlugin />
              <ListMaxIndentLevelPlugin maxDepth={7} />
              <TablePlugin
                hasCellMerge={tableCellMerge}
                hasCellBackgroundColor={tableCellBackgroundColor}
              />
              <TableCellResizer />
              <TableHoverActionsPlugin />
              <ImagesPlugin />
              <InlineImagePlugin />
              <LinkPlugin />
              <PollPlugin />
              <TwitterPlugin />
              <YouTubePlugin />
              <FigmaPlugin />
              <ClickableLinkPlugin disabled={isEditable} />
              <HorizontalRulePlugin />
              <TabFocusPlugin />
              <TabIndentationPlugin />
              <CollapsiblePlugin />
              <PageBreakPlugin />
              <LayoutPlugin />
              <CustomTextNodePlugin />
              <FirmwareTablePlugin />
              <CustomTranslateIconPlugin />
              {isEditable && floatingAnchorElem && !isSmallWidthViewport && (
                <>
                  <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
                  <FloatingLinkEditorPlugin
                    anchorElem={floatingAnchorElem}
                    isLinkEditMode={isLinkEditMode}
                    setIsLinkEditMode={setIsLinkEditMode}
                    linkEditorRef={linkEditorRef}
                  />
                  <TableCellActionMenuPlugin
                    anchorElem={floatingAnchorElem}
                    cellMerge={true}
                  />
                  <FloatingTextFormatToolbarPlugin
                    anchorElem={floatingAnchorElem}
                    setIsLinkEditMode={setIsLinkEditMode}
                  />
                </>
              )}
            </>
          ) : (
            <>
              <PlainTextPlugin
                contentEditable={
                  <ContentEditable
                    placeholder={isEditable ? placeholder : ''}
                  />
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
              <HistoryPlugin externalHistoryState={historyState} />
            </>
          )}
          {(isCharLimit || isCharLimitUtf8) && (
            <CharacterLimitPlugin
              charset={isCharLimit ? 'UTF-16' : 'UTF-8'}
              maxLength={5}
            />
          )}
          {isAutocomplete && <AutocompletePlugin />}
          <div>{showTableOfContents && <TableOfContentsPlugin />}</div>
          {shouldUseLexicalContextMenu && isEditable && <ContextMenuPlugin />}
        </div>
        {pageDesignerContext?.isVersionsModeActive && <VersionList />}
      </div>
    </>
  );
}
